import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Sync as SyncIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  EventNote as AttendanceIcon,
  Assessment as EnrollmentIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  CloudSync as CloudSyncIcon,
  Delete as ClearIcon,
  PlayArrow as PlayIcon,
  Speed as SpeedIcon,
  Build as BuildIcon,
  GppGood as GppGoodIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  CloudDone as CloudDoneIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Info as InfoIcon,
  BarChart as BarChartIcon,
  VerifiedUser as VerifiedUserIcon,
  DataObject as DataIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import SisMirrorStatus from '../../components/Admin/SisMirrorStatus';

// =============================================================================
// Types
// =============================================================================

interface SyncStatus {
  sis_connected: boolean;
  last_syncs: {
    enrollment: string | null;
    attendance: string | null;
    teachers: string | null;
    schools: string | null;
  };
  data_counts: {
    schools: number;
    students: number;
    teachers: number;
    today_attendance: number;
  };
}

interface ValidationResult {
  rule: string;
  status: 'pass' | 'fail' | 'warning';
  details: string;
  affected_records: number;
}

interface HistoryItem {
  id: string;
  timestamp: string;
  data_type: string;
  status: string;
  records_synced: number;
  duration_seconds: number;
  triggered_by: string;
  error_message?: string;
}

interface ScheduleItem {
  id: string;
  name: string;
  data_type: string;
  cron_expression: string;
  cron_label: string;
  status: string;
  next_run: string;
  last_run: string | null;
  created_by: string;
}

// =============================================================================
// Constants
// =============================================================================

const DATA_TYPES = ['enrollment', 'attendance', 'teachers', 'schools'];

const DATA_TYPE_COLORS: Record<string, string> = {
  enrollment: '#1976d2',
  attendance: '#2e7d32',
  teachers: '#ed6c02',
  schools: '#0288d1',
};

const DATA_TYPE_ICONS: Record<string, React.ReactNode> = {
  enrollment: <EnrollmentIcon />,
  attendance: <AttendanceIcon />,
  teachers: <PeopleIcon />,
  schools: <SchoolIcon />,
};

const CRON_PRESETS = [
  { label: 'Daily at 2 AM', value: '0 2 * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Every 12 hours', value: '0 */12 * * *' },
  { label: 'Weekly Sunday midnight', value: '0 0 * * 0' },
  { label: 'Monthly 1st at 3 AM', value: '0 3 1 * *' },
  { label: 'Custom', value: 'custom' },
];

// Demo data for history & analytics (fallback when API not ready)
const DEMO_HISTORY: HistoryItem[] = [
  { id: '1', timestamp: new Date(Date.now() - 3600000).toISOString(), data_type: 'enrollment', status: 'success', records_synced: 45230, duration_seconds: 34, triggered_by: 'Scheduler' },
  { id: '2', timestamp: new Date(Date.now() - 7200000).toISOString(), data_type: 'attendance', status: 'success', records_synced: 12450, duration_seconds: 18, triggered_by: 'Scheduler' },
  { id: '3', timestamp: new Date(Date.now() - 10800000).toISOString(), data_type: 'teachers', status: 'success', records_synced: 3200, duration_seconds: 8, triggered_by: 'Manual' },
  { id: '4', timestamp: new Date(Date.now() - 14400000).toISOString(), data_type: 'schools', status: 'success', records_synced: 892, duration_seconds: 5, triggered_by: 'Scheduler' },
  { id: '5', timestamp: new Date(Date.now() - 86400000).toISOString(), data_type: 'enrollment', status: 'failed', records_synced: 0, duration_seconds: 45, triggered_by: 'Scheduler', error_message: 'Connection timeout' },
  { id: '6', timestamp: new Date(Date.now() - 90000000).toISOString(), data_type: 'attendance', status: 'success', records_synced: 11890, duration_seconds: 22, triggered_by: 'Manual' },
  { id: '7', timestamp: new Date(Date.now() - 172800000).toISOString(), data_type: 'enrollment', status: 'success', records_synced: 44980, duration_seconds: 32, triggered_by: 'Scheduler' },
  { id: '8', timestamp: new Date(Date.now() - 176400000).toISOString(), data_type: 'teachers', status: 'success', records_synced: 3180, duration_seconds: 9, triggered_by: 'Scheduler' },
];

const DEMO_ANALYTICS_BAR = [
  { day: 'Mon', enrollment: 45230, attendance: 12450, teachers: 3200, schools: 892 },
  { day: 'Tue', enrollment: 44980, attendance: 11890, teachers: 0, schools: 0 },
  { day: 'Wed', enrollment: 45100, attendance: 12100, teachers: 3180, schools: 890 },
  { day: 'Thu', enrollment: 0, attendance: 12300, teachers: 0, schools: 0 },
  { day: 'Fri', enrollment: 45350, attendance: 12500, teachers: 3210, schools: 895 },
  { day: 'Sat', enrollment: 0, attendance: 0, teachers: 0, schools: 0 },
  { day: 'Sun', enrollment: 45400, attendance: 12600, teachers: 3220, schools: 893 },
];

const DEMO_ANALYTICS_LINE = [
  { week: 'Week 1', success_rate: 92, total_syncs: 28 },
  { week: 'Week 2', success_rate: 96, total_syncs: 30 },
  { week: 'Week 3', success_rate: 89, total_syncs: 26 },
  { week: 'Week 4', success_rate: 98, total_syncs: 32 },
];

const DEMO_SCHEDULES: ScheduleItem[] = [
  { id: '1', name: 'Daily Enrollment Sync', data_type: 'enrollment', cron_expression: '0 2 * * *', cron_label: 'Daily at 2:00 AM', status: 'active', next_run: new Date(Date.now() + 43200000).toISOString(), last_run: new Date(Date.now() - 43200000).toISOString(), created_by: 'admin' },
  { id: '2', name: 'Daily Attendance Sync', data_type: 'attendance', cron_expression: '0 3 * * *', cron_label: 'Daily at 3:00 AM', status: 'active', next_run: new Date(Date.now() + 46800000).toISOString(), last_run: new Date(Date.now() - 39600000).toISOString(), created_by: 'admin' },
  { id: '3', name: 'Weekly Teacher Sync', data_type: 'teachers', cron_expression: '0 4 * * 0', cron_label: 'Weekly Sunday at 4:00 AM', status: 'active', next_run: new Date(Date.now() + 259200000).toISOString(), last_run: new Date(Date.now() - 432000000).toISOString(), created_by: 'admin' },
  { id: '4', name: 'Monthly School Sync', data_type: 'schools', cron_expression: '0 3 1 * *', cron_label: 'Monthly 1st at 3:00 AM', status: 'paused', next_run: new Date(Date.now() + 1209600000).toISOString(), last_run: new Date(Date.now() - 1209600000).toISOString(), created_by: 'admin' },
];

// =============================================================================
// Helper Components
// =============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function getStatusChipColor(s: string): 'success' | 'error' | 'warning' | 'default' | 'info' {
  if (s === 'success' || s === 'active' || s === 'completed') return 'success';
  if (s === 'failed' || s === 'error') return 'error';
  if (s === 'warning' || s === 'partial' || s === 'paused') return 'warning';
  if (s === 'running' || s === 'in_progress') return 'info';
  return 'default';
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function getTimeSinceSync(ts: string | null): string {
  if (!ts) return 'Never';
  const diff = Date.now() - new Date(ts).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return `${Math.floor(diff / 60000)}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// =============================================================================
// Main Component
// =============================================================================

const DataSyncManager: React.FC = () => {
  const queryClient = useQueryClient();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false, message: '', severity: 'info',
  });

  // Sync output dialog
  const [syncOutput, setSyncOutput] = useState('');
  const [outputDialogOpen, setOutputDialogOpen] = useState(false);

  // Pipeline builder state
  const [pipelineStep, setPipelineStep] = useState(0);
  const [pipelineConfig, setPipelineConfig] = useState({
    dataSources: ['enrollment'] as string[],
    filters: { academic_year: new Date().getFullYear(), days_back: 7 },
    transform: { remove_duplicates: true, validate_formats: true, fill_missing: false },
    import_strategy: 'CREATE_AND_UPDATE',
    dry_run: true,
  });
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineResult, setPipelineResult] = useState<any>(null);

  // Validation state
  const [validationType, setValidationType] = useState('all');
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [qualityScore, setQualityScore] = useState<number | null>(null);
  const [validationRunning, setValidationRunning] = useState(false);
  const [validationOutliers, setValidationOutliers] = useState<any[]>([]);

  // Scheduler state
  const [schedules, setSchedules] = useState<ScheduleItem[]>(DEMO_SCHEDULES);
  const [addScheduleOpen, setAddScheduleOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '', data_type: 'enrollment', cron_preset: '0 2 * * *', custom_cron: '', active: true,
  });

  // History state
  const [historyFilter, setHistoryFilter] = useState({ data_type: 'all' });

  // Sync options
  const [enrollmentYear, setEnrollmentYear] = useState(new Date().getFullYear());
  const [attendanceDays, setAttendanceDays] = useState(7);
  const [clearExisting, setClearExisting] = useState(false);

  // ---------------------------------------------------------------------------
  // API Queries
  // ---------------------------------------------------------------------------

  const { data: syncStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery<SyncStatus>({
    queryKey: ['syncStatus'],
    queryFn: async () => {
      const response = await api.get('/sis-integration/sync-status/');
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: syncHistory } = useQuery({
    queryKey: ['syncHistory', historyFilter],
    queryFn: async () => {
      try {
        const response = await api.get('/sis-integration/sync/history/', {
          params: { limit: 50, data_type: historyFilter.data_type },
        });
        return response.data?.history || [];
      } catch {
        return DEMO_HISTORY;
      }
    },
  });

  const { data: syncAnalytics } = useQuery({
    queryKey: ['syncAnalytics'],
    queryFn: async () => {
      try {
        const response = await api.get('/sis-integration/sync/analytics/');
        const d = response.data;
        // Map backend field names to frontend expected names
        const summary = d.summary ? {
          total_syncs: d.summary.total_syncs,
          success_rate: d.summary.success_rate,
          avg_duration: d.summary.avg_duration_seconds || d.summary.avg_duration || 0,
          most_synced: d.summary.most_synced_type || d.summary.most_synced || 'enrollment',
        } : null;
        // Map daily_records: {date: "2026-02-02"} → {day: "Mon", ...}
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const barData = (d.daily_records || []).map((r: any) => ({
          ...r,
          day: days[new Date(r.date + 'T00:00:00').getDay()] || r.date,
        }));
        // Map success_trend: {date: "2026-01-10"} → {week: "Week 1", ...}
        const lineData = (d.success_trend || []).map((r: any, i: number) => ({
          ...r,
          week: `Week ${i + 1}`,
        }));
        return {
          bar_data: barData.length > 0 ? barData : DEMO_ANALYTICS_BAR,
          line_data: lineData.length > 0 ? lineData : DEMO_ANALYTICS_LINE,
          summary,
        };
      } catch {
        return { bar_data: DEMO_ANALYTICS_BAR, line_data: DEMO_ANALYTICS_LINE, summary: null };
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const syncEnrollmentMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/enrollment/', {
        academic_year: enrollmentYear, clear_existing: clearExisting,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'Enrollment sync completed!', severity: 'success' });
      setSyncOutput(data.output || 'Sync completed successfully');
      setOutputDialogOpen(true);
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Sync failed', severity: 'error' });
    },
  });

  const syncAttendanceMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/attendance/', { days_back: attendanceDays });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'Attendance sync completed!', severity: 'success' });
      setSyncOutput(data.output || 'Sync completed successfully');
      setOutputDialogOpen(true);
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Sync failed', severity: 'error' });
    },
  });

  const syncTeachersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/teachers/', { active_only: true });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'Teacher sync completed!', severity: 'success' });
      setSyncOutput(data.output || 'Sync completed successfully');
      setOutputDialogOpen(true);
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Sync failed', severity: 'error' });
    },
  });

  const syncSchoolsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/schools/', { active_only: true, include_infrastructure: true });
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'School sync completed!', severity: 'success' });
      setSyncOutput(data.output || 'Sync completed successfully');
      setOutputDialogOpen(true);
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Sync failed', severity: 'error' });
    },
  });

  const syncAllMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/all/');
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'Full sync completed!', severity: 'success' });
      setSyncOutput(JSON.stringify(data.results, null, 2));
      setOutputDialogOpen(true);
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Sync failed', severity: 'error' });
    },
  });

  const clearCacheMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/sis-integration/sync/clear-cache/');
      return response.data;
    },
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Cache cleared successfully!', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['liveDashboardStats'] });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to clear cache', severity: 'error' });
    },
  });

  const saveScheduleMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/sis-integration/sync/schedule/', data);
      return response.data;
    },
    onSuccess: (data) => {
      setSnackbar({ open: true, message: 'Schedule saved!', severity: 'success' });
      if (data.schedules) setSchedules(data.schedules);
      setAddScheduleOpen(false);
      setNewSchedule({ name: '', data_type: 'enrollment', cron_preset: '0 2 * * *', custom_cron: '', active: true });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Schedule saved locally (backend pending)', severity: 'info' });
      // Save locally as fallback
      const cronExpr = newSchedule.cron_preset === 'custom' ? newSchedule.custom_cron : newSchedule.cron_preset;
      const preset = CRON_PRESETS.find((p) => p.value === cronExpr);
      const newItem: ScheduleItem = {
        id: String(Date.now()),
        name: newSchedule.name,
        data_type: newSchedule.data_type,
        cron_expression: cronExpr,
        cron_label: preset ? preset.label : cronExpr,
        status: newSchedule.active ? 'active' : 'paused',
        next_run: new Date(Date.now() + 86400000).toISOString(),
        last_run: null,
        created_by: 'admin',
      };
      setSchedules((prev) => [...prev, newItem]);
      setAddScheduleOpen(false);
      setNewSchedule({ name: '', data_type: 'enrollment', cron_preset: '0 2 * * *', custom_cron: '', active: true });
    },
  });

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const isAnySyncRunning =
    syncEnrollmentMutation.isPending ||
    syncAttendanceMutation.isPending ||
    syncTeachersMutation.isPending ||
    syncSchoolsMutation.isPending ||
    syncAllMutation.isPending ||
    pipelineRunning;

  const formatLastSync = (ts: string | null) => {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleString();
  };

  const pipelineHealthStatus = useMemo(() => {
    if (!syncStatus) return { color: 'default' as const, label: 'Unknown' };
    const lastSyncs = syncStatus.last_syncs;
    const hasRecent = Object.values(lastSyncs).some((ts) => {
      if (!ts) return false;
      return Date.now() - new Date(ts).getTime() < 86400000;
    });
    if (syncStatus.sis_connected && hasRecent) return { color: 'success' as const, label: 'Healthy' };
    if (syncStatus.sis_connected) return { color: 'warning' as const, label: 'Stale' };
    return { color: 'error' as const, label: 'Disconnected' };
  }, [syncStatus]);

  const dataFreshnessHours = useMemo(() => {
    if (!syncStatus) return null;
    const times = Object.values(syncStatus.last_syncs).filter(Boolean) as string[];
    if (times.length === 0) return null;
    const mostRecent = Math.max(...times.map((t) => new Date(t).getTime()));
    return Math.round((Date.now() - mostRecent) / 3600000);
  }, [syncStatus]);

  const totalRecords = useMemo(() => {
    if (!syncStatus?.data_counts) return 0;
    const c = syncStatus.data_counts;
    return (c.schools || 0) + (c.students || 0) + (c.teachers || 0) + (c.today_attendance || 0);
  }, [syncStatus]);

  const historyDisplayData = useMemo(() => {
    const data = syncHistory && Array.isArray(syncHistory) && syncHistory.length > 0 ? syncHistory : DEMO_HISTORY;
    if (historyFilter.data_type !== 'all') {
      return data.filter((item: HistoryItem) => item.data_type === historyFilter.data_type);
    }
    return data;
  }, [syncHistory, historyFilter]);

  const historySummary = useMemo(() => {
    const data = historyDisplayData;
    const total = data.length;
    const successes = data.filter((d: HistoryItem) => d.status === 'success').length;
    const avgDur = total > 0 ? Math.round(data.reduce((a: number, d: HistoryItem) => a + d.duration_seconds, 0) / total) : 0;
    const typeCounts: Record<string, number> = {};
    data.forEach((d: HistoryItem) => { typeCounts[d.data_type] = (typeCounts[d.data_type] || 0) + 1; });
    const mostSynced = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'enrollment';
    return {
      total_syncs: total,
      success_rate: total > 0 ? Math.round((successes / total) * 100) : 100,
      avg_duration: avgDur,
      most_synced: mostSynced,
    };
  }, [historyDisplayData]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRunValidation = async () => {
    setValidationRunning(true);
    setValidationResults([]);
    setQualityScore(null);
    setValidationOutliers([]);
    try {
      const response = await api.post('/sis-integration/sync/validate/', {
        data_type: validationType, options: {},
      });
      const data = response.data;
      setValidationResults(data.validations || []);
      setQualityScore(data.quality_score ?? null);
      setValidationOutliers(data.outliers || []);
      setSnackbar({ open: true, message: `Validation complete! Quality score: ${data.quality_score}%`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Validation failed', severity: 'error' });
    } finally {
      setValidationRunning(false);
    }
  };

  const handleRunPipeline = async () => {
    setPipelineRunning(true);
    setPipelineResult(null);
    try {
      const response = await api.post('/sis-integration/sync/pipeline/', {
        pipeline_config: {
          data_sources: pipelineConfig.dataSources,
          filters: pipelineConfig.filters,
          transform: pipelineConfig.transform,
          import_strategy: pipelineConfig.import_strategy,
          dry_run: pipelineConfig.dry_run,
        },
      });
      setPipelineResult(response.data);
      const msg = pipelineConfig.dry_run ? 'Dry run complete!' : 'Pipeline executed successfully!';
      setSnackbar({ open: true, message: msg, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Pipeline failed', severity: 'error' });
    } finally {
      setPipelineRunning(false);
    }
  };

  const handleToggleSchedule = (id: string) => {
    setSchedules((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s))
    );
  };

  const handleManualTrigger = (dataType: string) => {
    switch (dataType) {
      case 'enrollment': syncEnrollmentMutation.mutate(); break;
      case 'attendance': syncAttendanceMutation.mutate(); break;
      case 'teachers': syncTeachersMutation.mutate(); break;
      case 'schools': syncSchoolsMutation.mutate(); break;
      default: break;
    }
  };

  const handleAddSchedule = () => {
    const cronExpr = newSchedule.cron_preset === 'custom' ? newSchedule.custom_cron : newSchedule.cron_preset;
    saveScheduleMutation.mutate({
      name: newSchedule.name,
      data_type: newSchedule.data_type,
      cron_expression: cronExpr,
      is_active: newSchedule.active,
    });
  };

  const toggleDataSource = (source: string) => {
    setPipelineConfig((prev) => {
      const ds = prev.dataSources.includes(source)
        ? prev.dataSources.filter((s) => s !== source)
        : [...prev.dataSources, source];
      return { ...prev, dataSources: ds };
    });
  };

  // ---------------------------------------------------------------------------
  // Render: Tab 1 - Pipeline Dashboard
  // ---------------------------------------------------------------------------

  const renderPipelineDashboard = () => (
    <Box>
      {/* What has actually been copied from SIS. Put first because a wrong
          figure anywhere else on this page is usually explained here. */}
      <Box sx={{ mb: 3 }}>
        <SisMirrorStatus />
      </Box>

      {/* Health Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SpeedIcon sx={{ fontSize: 36, color: `${pipelineHealthStatus.color}.main`, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                <Chip label={pipelineHealthStatus.label} color={pipelineHealthStatus.color} size="small" />
              </Typography>
              <Typography variant="body2" color="text.secondary">Pipeline Health</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimerIcon sx={{ fontSize: 36, color: (dataFreshnessHours ?? 999) < 24 ? 'success.main' : 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color={(dataFreshnessHours ?? 999) < 24 ? 'success.main' : 'warning.main'}>
                {dataFreshnessHours !== null ? `${dataFreshnessHours}h` : '--'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Data Freshness</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {totalRecords > 0 ? totalRecords.toLocaleString() : '--'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total Records</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <VerifiedUserIcon sx={{ fontSize: 36, color: qualityScore && qualityScore >= 80 ? 'success.main' : 'info.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color={qualityScore && qualityScore >= 80 ? 'success.main' : 'info.main'}>
                {qualityScore !== null ? `${qualityScore}%` : '--'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Quality Score</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pipeline Status Timeline */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DataIcon /> Pipeline Status
        </Typography>
        <Grid container spacing={2}>
          {DATA_TYPES.map((type) => {
            const lastSync = syncStatus?.last_syncs?.[type as keyof typeof syncStatus.last_syncs] || null;
            const count = type === 'enrollment' ? syncStatus?.data_counts?.students
              : type === 'attendance' ? syncStatus?.data_counts?.today_attendance
              : type === 'teachers' ? syncStatus?.data_counts?.teachers
              : syncStatus?.data_counts?.schools;

            return (
              <Grid item xs={12} sm={6} md={3} key={type}>
                <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: DATA_TYPE_COLORS[type] }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {DATA_TYPE_ICONS[type]}
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                        {type}
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: DATA_TYPE_COLORS[type] }}>
                      {count?.toLocaleString() || '--'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      records
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {getTimeSinceSync(lastSync)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSyncIcon /> Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={syncAllMutation.isPending ? <CircularProgress size={20} /> : <CloudSyncIcon />}
              onClick={() => syncAllMutation.mutate()}
              disabled={isAnySyncRunning}
              sx={{ py: 2 }}
            >
              Run Full Pipeline
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              color="info"
              startIcon={validationRunning ? <CircularProgress size={20} /> : <GppGoodIcon />}
              onClick={handleRunValidation}
              disabled={isAnySyncRunning || validationRunning}
              sx={{ py: 2 }}
            >
              Validate All Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              color="error"
              startIcon={clearCacheMutation.isPending ? <CircularProgress size={20} /> : <ClearIcon />}
              onClick={() => clearCacheMutation.mutate()}
              disabled={isAnySyncRunning}
              sx={{ py: 2 }}
            >
              Clear Cache
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render: Tab 2 - Pipeline Builder
  // ---------------------------------------------------------------------------

  const renderPipelineBuilder = () => (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon /> ETL Pipeline Builder
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure and execute a DHIS2-style Extract-Transform-Load pipeline
        </Typography>

        <Stepper activeStep={pipelineStep} orientation="vertical">
          {/* Step 1: Extract */}
          <Step>
            <StepLabel>
              <Typography variant="subtitle1" fontWeight="bold">Extract - Select Data Sources</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select data sources to extract from the SIS database
                </Typography>
                <Grid container spacing={2}>
                  {DATA_TYPES.map((type) => (
                    <Grid item xs={6} sm={3} key={type}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          border: 2,
                          borderColor: pipelineConfig.dataSources.includes(type) ? DATA_TYPE_COLORS[type] : 'divider',
                          bgcolor: pipelineConfig.dataSources.includes(type) ? alpha(DATA_TYPE_COLORS[type], 0.05) : 'transparent',
                        }}
                        onClick={() => toggleDataSource(type)}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Checkbox checked={pipelineConfig.dataSources.includes(type)} sx={{ color: DATA_TYPE_COLORS[type] }} />
                          <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                            {type}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Filters</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Academic Year"
                    type="number"
                    size="small"
                    value={pipelineConfig.filters.academic_year}
                    onChange={(e) => setPipelineConfig((prev) => ({
                      ...prev,
                      filters: { ...prev.filters, academic_year: parseInt(e.target.value) },
                    }))}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="Days Back"
                    type="number"
                    size="small"
                    value={pipelineConfig.filters.days_back}
                    onChange={(e) => setPipelineConfig((prev) => ({
                      ...prev,
                      filters: { ...prev.filters, days_back: parseInt(e.target.value) },
                    }))}
                    sx={{ width: 120 }}
                    inputProps={{ min: 1, max: 365 }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={() => setPipelineStep(1)} disabled={pipelineConfig.dataSources.length === 0}>
                  Next: Transform
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Transform */}
          <Step>
            <StepLabel>
              <Typography variant="subtitle1" fontWeight="bold">Transform - Data Cleansing & Strategy</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Data Cleansing Options</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pipelineConfig.transform.remove_duplicates}
                        onChange={(e) => setPipelineConfig((prev) => ({
                          ...prev,
                          transform: { ...prev.transform, remove_duplicates: e.target.checked },
                        }))}
                      />
                    }
                    label="Remove duplicate records"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pipelineConfig.transform.validate_formats}
                        onChange={(e) => setPipelineConfig((prev) => ({
                          ...prev,
                          transform: { ...prev.transform, validate_formats: e.target.checked },
                        }))}
                      />
                    }
                    label="Validate data formats"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pipelineConfig.transform.fill_missing}
                        onChange={(e) => setPipelineConfig((prev) => ({
                          ...prev,
                          transform: { ...prev.transform, fill_missing: e.target.checked },
                        }))}
                      />
                    }
                    label="Fill missing values with defaults"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>Import Strategy</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Import Strategy</InputLabel>
                  <Select
                    value={pipelineConfig.import_strategy}
                    label="Import Strategy"
                    onChange={(e) => setPipelineConfig((prev) => ({ ...prev, import_strategy: e.target.value }))}
                  >
                    <MenuItem value="CREATE">CREATE - Only insert new records</MenuItem>
                    <MenuItem value="UPDATE">UPDATE - Only update existing records</MenuItem>
                    <MenuItem value="CREATE_AND_UPDATE">CREATE_AND_UPDATE - Insert new and update existing</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={pipelineConfig.dry_run}
                      onChange={(e) => setPipelineConfig((prev) => ({ ...prev, dry_run: e.target.checked }))}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Dry Run Mode {pipelineConfig.dry_run ? '(ON)' : '(OFF)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Preview changes without writing to database
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setPipelineStep(0)}>Back</Button>
                <Button variant="contained" onClick={() => setPipelineStep(2)}>Next: Load</Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Load */}
          <Step>
            <StepLabel>
              <Typography variant="subtitle1" fontWeight="bold">Load - Review & Execute</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Alert severity={pipelineConfig.dry_run ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {pipelineConfig.dry_run
                    ? 'DRY RUN MODE - No data will be written to the database'
                    : 'LIVE MODE - Data will be written to the database'}
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Pipeline Summary</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Data Sources</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {pipelineConfig.dataSources.map((ds) => (
                          <Chip key={ds} label={ds} size="small" sx={{ bgcolor: alpha(DATA_TYPE_COLORS[ds], 0.1), color: DATA_TYPE_COLORS[ds], textTransform: 'capitalize' }} />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Import Strategy</Typography>
                      <Typography variant="body2" fontWeight="bold">{pipelineConfig.import_strategy}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Cleansing</Typography>
                      <Typography variant="body2">
                        {[
                          pipelineConfig.transform.remove_duplicates && 'Dedup',
                          pipelineConfig.transform.validate_formats && 'Validate',
                          pipelineConfig.transform.fill_missing && 'Fill',
                        ].filter(Boolean).join(', ') || 'None'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {pipelineResult && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Pipeline Result</Typography>
                    {pipelineResult.stages?.map((stage: any, idx: number) => (
                      <Box key={idx} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={stage.name} size="small" color={stage.status === 'completed' ? 'success' : 'error'} />
                          <Typography variant="body2">
                            {stage.records_processed?.toLocaleString()} records processed
                            {stage.duration_seconds !== undefined && ` in ${stage.duration_seconds}s`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {pipelineResult.summary && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          Extracted: {pipelineResult.summary.total_extracted?.toLocaleString()} |
                          Transformed: {pipelineResult.summary.total_transformed?.toLocaleString()} |
                          Loaded: {pipelineResult.summary.total_loaded?.toLocaleString()} |
                          Quality: {pipelineResult.summary.quality_score}%
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setPipelineStep(1)}>Back</Button>
                <Button
                  variant="contained"
                  color={pipelineConfig.dry_run ? 'warning' : 'primary'}
                  startIcon={pipelineRunning ? <CircularProgress size={20} /> : <PlayIcon />}
                  onClick={handleRunPipeline}
                  disabled={isAnySyncRunning}
                >
                  {pipelineConfig.dry_run ? 'Run Dry Run' : 'Execute Pipeline'}
                </Button>
                <Button onClick={() => { setPipelineStep(0); setPipelineResult(null); }}>Reset</Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render: Tab 3 - Data Validation & Quality Gate
  // ---------------------------------------------------------------------------

  const renderValidation = () => (
    <Box>
      {/* Quality Score Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={qualityScore ?? 0}
                size={140}
                thickness={6}
                sx={{
                  color: (qualityScore ?? 0) >= 90 ? 'success.main' : (qualityScore ?? 0) >= 70 ? 'warning.main' : 'error.main',
                }}
              />
              <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold">
                  {qualityScore !== null ? `${qualityScore}%` : '--'}
                </Typography>
              </Box>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
              Data Quality Score
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="h6" gutterBottom>Run Data Validation</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Validate SIS data quality before syncing. Checks for completeness, format, range, duplicates, and cross-references.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Data Type</InputLabel>
                <Select
                  value={validationType}
                  label="Data Type"
                  onChange={(e) => setValidationType(e.target.value)}
                >
                  <MenuItem value="all">All Data Types</MenuItem>
                  {DATA_TYPES.map((dt) => (
                    <MenuItem key={dt} value={dt} sx={{ textTransform: 'capitalize' }}>
                      {dt.charAt(0).toUpperCase() + dt.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={validationRunning ? <CircularProgress size={20} /> : <GppGoodIcon />}
                onClick={handleRunValidation}
                disabled={validationRunning}
              >
                Run Validation
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Validation Results Table */}
      {validationResults.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GppGoodIcon /> Validation Results
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rule</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Affected Records</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validationResults.map((result, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                        {result.rule}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={result.status}
                        size="small"
                        color={getStatusChipColor(result.status)}
                        icon={result.status === 'pass' ? <SuccessIcon /> : result.status === 'fail' ? <ErrorIcon /> : <WarningIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{result.details}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color={result.affected_records > 0 ? 'error.main' : 'text.secondary'}>
                        {result.affected_records.toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Outliers */}
      {validationOutliers.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Outlier Detection
          </Typography>
          <Grid container spacing={2}>
            {validationOutliers.map((outlier: any, idx: number) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold">{outlier.field}</Typography>
                    <Typography variant="body2" color="error.main">{outlier.value}</Typography>
                    <Typography variant="caption" color="text.secondary">Expected: {outlier.expected_range}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render: Tab 4 - Sync Scheduler
  // ---------------------------------------------------------------------------

  const renderScheduler = () => (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon /> Scheduled Sync Jobs
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddScheduleOpen(true)}>
            Add Schedule
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Schedule</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Next Run</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Run</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{schedule.name}</Typography>
                    <Typography variant="caption" color="text.secondary">by {schedule.created_by}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={schedule.data_type}
                      size="small"
                      sx={{
                        bgcolor: alpha(DATA_TYPE_COLORS[schedule.data_type] || '#666', 0.1),
                        color: DATA_TYPE_COLORS[schedule.data_type] || '#666',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{schedule.cron_label}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {schedule.cron_expression}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{new Date(schedule.next_run).toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {schedule.last_run ? new Date(schedule.last_run).toLocaleString() : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={schedule.status === 'active'}
                          onChange={() => handleToggleSchedule(schedule.id)}
                          size="small"
                          color="success"
                        />
                      }
                      label={<Chip label={schedule.status} size="small" color={getStatusChipColor(schedule.status)} variant="outlined" />}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`Trigger ${schedule.data_type} sync now`}>
                      <IconButton size="small" onClick={() => handleManualTrigger(schedule.data_type)} disabled={isAnySyncRunning} color="primary">
                        <PlayIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {schedules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No scheduled jobs configured. Click "Add Schedule" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cron Presets Reference */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="info" /> Schedule Presets Reference
        </Typography>
        <Grid container spacing={2}>
          {CRON_PRESETS.filter((p) => p.value !== 'custom').map((preset) => (
            <Grid item xs={12} sm={6} md={4} key={preset.value}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="body2" fontWeight="bold">{preset.label}</Typography>
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">{preset.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Add Schedule Dialog */}
      <Dialog open={addScheduleOpen} onClose={() => setAddScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Schedule</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Schedule Name"
              fullWidth
              value={newSchedule.name}
              onChange={(e) => setNewSchedule((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Daily Attendance Sync"
            />
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={newSchedule.data_type}
                label="Data Type"
                onChange={(e) => setNewSchedule((prev) => ({ ...prev, data_type: e.target.value }))}
              >
                {DATA_TYPES.map((dt) => (
                  <MenuItem key={dt} value={dt} sx={{ textTransform: 'capitalize' }}>
                    {dt.charAt(0).toUpperCase() + dt.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Schedule Preset</InputLabel>
              <Select
                value={newSchedule.cron_preset}
                label="Schedule Preset"
                onChange={(e) => setNewSchedule((prev) => ({ ...prev, cron_preset: e.target.value }))}
              >
                {CRON_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                    {preset.value !== 'custom' && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({preset.value})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newSchedule.cron_preset === 'custom' && (
              <TextField
                label="Custom Cron Expression"
                fullWidth
                value={newSchedule.custom_cron}
                onChange={(e) => setNewSchedule((prev) => ({ ...prev, custom_cron: e.target.value }))}
                placeholder="* * * * *"
                helperText="Format: minute hour day-of-month month day-of-week"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={newSchedule.active}
                  onChange={(e) => setNewSchedule((prev) => ({ ...prev, active: e.target.checked }))}
                  color="success"
                />
              }
              label="Active immediately"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddScheduleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSchedule}
            disabled={saveScheduleMutation.isPending || !newSchedule.name}
            startIcon={saveScheduleMutation.isPending ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Save Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render: Tab 5 - Sync History & Analytics
  // ---------------------------------------------------------------------------

  const renderHistory = () => {
    const analytics = syncAnalytics || { bar_data: DEMO_ANALYTICS_BAR, line_data: DEMO_ANALYTICS_LINE, summary: null };
    const summary = analytics.summary || historySummary;

    return (
      <Box>
        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">{summary.total_syncs}</Typography>
                <Typography variant="body2" color="text.secondary">Total Syncs</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color={summary.success_rate >= 90 ? 'success.main' : 'warning.main'}>
                  {summary.success_rate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="info.main">{formatDuration(summary.avg_duration)}</Typography>
                <Typography variant="body2" color="text.secondary">Avg Duration</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Chip
                  label={summary.most_synced}
                  sx={{
                    bgcolor: alpha(DATA_TYPE_COLORS[summary.most_synced] || '#666', 0.1),
                    color: DATA_TYPE_COLORS[summary.most_synced] || '#666',
                    fontWeight: 'bold', textTransform: 'capitalize', fontSize: '1rem', height: 36, mb: 0.5,
                  }}
                />
                <Typography variant="body2" color="text.secondary">Most Synced</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon /> Records Synced (Last 7 Days)
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.bar_data || DEMO_ANALYTICS_BAR}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="enrollment" fill="#1976d2" name="Enrollment" />
                    <Bar dataKey="attendance" fill="#2e7d32" name="Attendance" />
                    <Bar dataKey="teachers" fill="#ed6c02" name="Teachers" />
                    <Bar dataKey="schools" fill="#0288d1" name="Schools" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon /> Sync Success Rate Over Time
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.line_data || DEMO_ANALYTICS_LINE}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis domain={[0, 100]} />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success_rate" stroke="#2e7d32" strokeWidth={2} name="Success Rate (%)" dot={{ fill: '#2e7d32', r: 4 }} />
                    <Line type="monotone" dataKey="total_syncs" stroke="#1976d2" strokeWidth={2} name="Total Syncs" dot={{ fill: '#1976d2', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Sync Log Table */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Sync Log
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Data Type</InputLabel>
                <Select
                  value={historyFilter.data_type}
                  label="Data Type"
                  onChange={(e) => setHistoryFilter((prev) => ({ ...prev, data_type: e.target.value }))}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {DATA_TYPES.map((dt) => (
                    <MenuItem key={dt} value={dt} sx={{ textTransform: 'capitalize' }}>
                      {dt.charAt(0).toUpperCase() + dt.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Refresh history">
                <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['syncHistory'] })} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Records Synced</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Triggered By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyDisplayData.map((item: HistoryItem) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2">{new Date(item.timestamp).toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.data_type}
                        size="small"
                        sx={{
                          bgcolor: alpha(DATA_TYPE_COLORS[item.data_type] || '#666', 0.1),
                          color: DATA_TYPE_COLORS[item.data_type] || '#666',
                          fontWeight: 'bold', textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={getStatusChipColor(item.status)}
                        variant="outlined"
                        icon={item.status === 'success' ? <SuccessIcon /> : item.status === 'failed' ? <ErrorIcon /> : <WarningIcon />}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{item.records_synced.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatDuration(item.duration_seconds)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{item.triggered_by}</Typography>
                      {item.error_message && (
                        <Typography variant="caption" color="error">{item.error_message}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {historyDisplayData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No sync history found for the selected filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Data Sync Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            DHIS2-inspired ETL pipeline for SIS data synchronization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh Status">
            <IconButton onClick={() => refetchStatus()} disabled={statusLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Chip
            icon={syncStatus?.sis_connected ? <CloudDoneIcon /> : <ErrorIcon />}
            label={syncStatus?.sis_connected ? 'SIS Connected' : 'SIS Disconnected'}
            color={syncStatus?.sis_connected ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Global progress bar */}
      {isAnySyncRunning && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Sync in progress... Please wait.
          </Typography>
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newVal) => setActiveTab(newVal)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minHeight: 56 } }}
        >
          <Tab icon={<SpeedIcon />} iconPosition="start" label="Pipeline Dashboard" />
          <Tab icon={<BuildIcon />} iconPosition="start" label="Pipeline Builder" />
          <Tab icon={<GppGoodIcon />} iconPosition="start" label="Data Validation" />
          <Tab icon={<ScheduleIcon />} iconPosition="start" label="Scheduler" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="History & Analytics" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>{renderPipelineDashboard()}</TabPanel>
      <TabPanel value={activeTab} index={1}>{renderPipelineBuilder()}</TabPanel>
      <TabPanel value={activeTab} index={2}>{renderValidation()}</TabPanel>
      <TabPanel value={activeTab} index={3}>{renderScheduler()}</TabPanel>
      <TabPanel value={activeTab} index={4}>{renderHistory()}</TabPanel>

      {/* Sync Output Dialog */}
      <Dialog open={outputDialogOpen} onClose={() => setOutputDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Sync Output
          <IconButton size="small" onClick={() => setOutputDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
            {syncOutput || 'No output available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutputDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataSyncManager;
